'use client';

import GetSlash from '@modules/get-slash-internet-card/lib/get-slash';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { GetSlashCredentials } from '@lib/third-parties/get-slash.types';
import type {
  StatusResponse,
  BookingListResponse,
} from '@modules/get-slash-internet-card/lib/get-slash';

export enum GetSlashStartSessionStatus {
  Success = 'success',
  NoTariff = 'no_tariff',
  DeviceLimitExceeded = 'device_limit_exceeded',
  GeneralError = 'general_error',
}

export type StartSessionResult = {
  status: GetSlashStartSessionStatus;
  maxClients?: number;
};

/**
 * Extended status response with additional information about booked tariffs
 */
export type GetSlashInternetSession = StatusResponse & {
  hasBookedTariff: boolean;
};

/**
 * State returned by the useGetSlashInternet hook
 */
export type GetSlashInternetState = {
  ready: boolean;
  credentialsValid: boolean | undefined;
  startSession: () => Promise<StartSessionResult>;
  endSession: () => Promise<boolean>;
  refreshSession: () => Promise<void>;
  session: GetSlashInternetSession | undefined;
};

/**
 * Select the optimal booking from a list of bookings.
 *
 * Selection logic:
 * 1. If only one tariff is returned, select the first one
 * 2. If more than one is returned, select the first one where remainingVolume is either undefined or greater than 0
 * 3. If no such tariff exists, select the first one
 */
const selectOptimalBooking = (bookings: BookingListResponse['bookings']) => {
  if (!bookings || bookings.length === 0) {
    return undefined;
  }
  if (bookings.length === 1) {
    return bookings[0];
  }

  // Find first booking with available volume or time (anything greater than 0)
  // - 0 means exhausted
  // - -1 means not applicable (e.g. remainingVolume for time-based tariffs, or remainingTime for volume-based tariffs)
  const availableBooking = bookings.find(
    booking => booking.remainingVolume > 0 || booking.remainingTime > 0,
  );

  return availableBooking ?? bookings[0];
};

/**
 * Hook for managing internet sessions on board.
 * Provides functions to start, end, and refresh internet sessions,
 * as well as access to the current session state.
 */
export const useGetSlashInternet = (args: {
  credentials: GetSlashCredentials | undefined;
  useDebugEndpoints?: boolean;
}): GetSlashInternetState => {
  const { credentials, useDebugEndpoints } = args;

  const [status, setStatus] = useState<GetSlashInternetSession>();
  const [credentialsValid, setCredentialsValid] = useState<boolean>();

  const controller = useMemo(() => {
    if (typeof credentials === 'undefined') {
      return undefined;
    }

    return new GetSlash({
      credentials,
      useDebugEndpoints,
    });
  }, [credentials, useDebugEndpoints]);

  const handleUpdate = useCallback(async () => {
    if (typeof controller === 'undefined') {
      return;
    }

    const status = await controller.status();

    if (!status.active) {
      const bookingList = await controller.bookingList();
      const relatedBooking = selectOptimalBooking(bookingList.bookings);

      setStatus({
        ...status,
        hasBookedTariff: typeof relatedBooking !== 'undefined',
        tariffName: relatedBooking?.tariffName,
        maxVolume: relatedBooking?.maxVolume,
        usedVolume: status.usedVolume ?? relatedBooking?.usedVolume,
        remainingVolume: relatedBooking?.remainingVolume,
        remainingTime: relatedBooking?.remainingTime,
      });

      return;
    }

    if (
      typeof status.usedVolume === 'undefined' ||
      typeof status.tariffName === 'undefined'
    ) {
      const bookingList = await controller.bookingList();
      const relatedBooking = bookingList.bookings.find(
        ({ id }) => id === status.bookingId,
      );

      setStatus({
        ...status,
        hasBookedTariff: typeof relatedBooking !== 'undefined',
        usedVolume: status.usedVolume ?? relatedBooking?.usedVolume,
        tariffName: status.tariffName ?? relatedBooking?.tariffName,
      });

      return;
    }

    setStatus({
      ...status,
      hasBookedTariff: typeof status.bookingId !== 'undefined',
    });
  }, [controller]);

  useEffect(() => {
    if (typeof credentialsValid !== 'undefined') {
      return;
    }

    controller?.validateCredentials().then(valid => setCredentialsValid(valid));
  }, [controller, credentialsValid]);

  return {
    ready: typeof status !== 'undefined',
    credentialsValid,
    startSession: async () => {
      if (typeof controller === 'undefined') {
        return { status: GetSlashStartSessionStatus.GeneralError };
      }

      const { bookings } = await controller.bookingList();
      const optimalBooking = selectOptimalBooking(bookings);
      const id = optimalBooking?.id;
      const maxClients = optimalBooking?.maxClients;

      if (typeof id === 'undefined') {
        handleUpdate();
        return { status: GetSlashStartSessionStatus.NoTariff };
      }

      const response = await controller.login({
        bookingId: id,
      });

      handleUpdate();

      if (response.success) {
        return { status: GetSlashStartSessionStatus.Success };
      }

      // Check if it's a device limit error
      if (
        response.error?.message?.includes('Max allowed clients') ||
        response.error?.message?.includes('Maximum number of allowed clients')
      ) {
        return {
          status: GetSlashStartSessionStatus.DeviceLimitExceeded,
          maxClients,
        };
      }

      return { status: GetSlashStartSessionStatus.GeneralError };
    },
    endSession: async () => {
      if (typeof controller === 'undefined') {
        return false;
      }

      const response = await controller.logout();

      handleUpdate();

      return response.success;
    },
    refreshSession: handleUpdate,
    session: status,
  };
};
